import { useState, type FormEvent } from 'react'
import type { ScalabilityConfiguration } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Field } from '@/components/ui/Field'
import { Icons } from '@/components/ui/Icons'
import { Check } from './Check'
import { NumberInput } from './NumberInput'
import { RuleEditor } from './RuleEditor'
import { newRule, toConfiguration, toDraft, validate, type RuleDraft } from './scalability'
import styles from './ScalabilityDialog.module.css'

export const SCALABILITY_FORM_ID = 'scalability-form'

interface ScalabilityFormProps {
  initial: ScalabilityConfiguration
  onSave: (configuration: ScalabilityConfiguration) => void
}

/** Edits a draft copy of the configuration; the dialog's Save button submits it through the form id. */
export function ScalabilityForm({ initial, onSave }: ScalabilityFormProps) {
  const [draft, setDraft] = useState(() => toDraft(initial))
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<number | null>(null)

  const setRule = (index: number, patch: Partial<RuleDraft>) =>
    setDraft((current) => ({ ...current, rules: current.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)) }))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const problem = validate(draft)
    setError(problem)
    if (!problem) onSave(toConfiguration(draft, initial))
  }

  return (
    <>
      <form id={SCALABILITY_FORM_ID} className={styles.form} onSubmit={submit} noValidate>
        <div className={styles.settings}>
          <div className={styles.span2}>
            <Check checked={draft.isActive} onChange={(isActive) => setDraft({ ...draft, isActive })}>
              Autoscale active
            </Check>
          </div>
          <Field label="Minimum instances" htmlFor="scalability-min">
            <NumberInput id="scalability-min" min={0} value={draft.minInstances} onChange={(minInstances) => setDraft({ ...draft, minInstances })} />
          </Field>
          <Field label="Maximum instances" htmlFor="scalability-max">
            <NumberInput id="scalability-max" min={0} value={draft.maxInstances} onChange={(maxInstances) => setDraft({ ...draft, maxInstances })} />
          </Field>
        </div>

        <h3 className={styles.rulesTitle}>Scale conditions</h3>
        {draft.rules.length === 0 && <p className="u-muted">No scale condition yet.</p>}
        {draft.rules.map((rule, index) => (
          <RuleEditor key={rule.key} rule={rule} position={index + 1} onChange={(patch) => setRule(index, patch)} onDelete={() => setDeleting(index)} />
        ))}
        <div>
          <Button icon={<Icons.Plus size={12} />} onClick={() => setDraft({ ...draft, rules: [...draft.rules, newRule()] })}>
            Add a scale condition
          </Button>
        </div>

        {error && (
          <div role="alert" className="u-danger">
            {error}
          </div>
        )}
      </form>

      {deleting !== null && (
        <ConfirmDialog
          title="Delete this scale condition"
          message={`${draft.rules[deleting]?.name.trim() || `Rule ${deleting + 1}`} is removed from the configuration once you save.`}
          confirmLabel="Delete"
          danger
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            setDraft({ ...draft, rules: draft.rules.filter((_, i) => i !== deleting) })
            setDeleting(null)
          }}
        />
      )}
    </>
  )
}
